// Round H — de shuffle loopt de HELE lijst met vragen af voordat een vraag terugkomt.
// Vragen zonder functie gelden voor iedereen en zitten in dezelfde groep als je eigen functie.
export const PICK_SIZE = 3;

export function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const hasFunctie = (q) => ((q.functies || []).length > 0);

const forMe = (q, title) => {
  const funcs = q.functies || [];
  if (!funcs.length) return true;
  return !!title && funcs.some((f) => (f || "").trim().toLowerCase() === title);
};

// Open vragen eerst, beantwoorde erachter. Binnen de open vragen zijn "mijn functie" en
// "voor iedereen" één geschudde groep; alleen bij de eerste weergave krijgt een vraag voor
// je eigen functie voorrang op de uitgelichte plek. Round K: een bedrijfsleider krijgt de
// locatievragen die voor zijn vestiging nog open zijn (site_open) als allereerste.
// Round N: focusvragen van het lopende seizoen (is_focus) komen direct na de locatievragen;
// een locatievraag die ook focus is gaat helemaal voorop. Alleen voor vragen van "mij".
export function orderPool(pool, userTitle, preferTargeted) {
  const title = (userTitle || "").trim().toLowerCase();
  const mine = (q) => forMe(q, title);
  // Review fix (K): a site question leads only when it is also for my functie.
  const isSiteFirst = (q) => !q.answered && q.site_open && mine(q);
  const isFocusFirst = (q) => !q.answered && q.is_focus && mine(q);
  const lead = (q) => isSiteFirst(q) || isFocusFirst(q);
  const open = pool.filter((q) => !q.answered && !lead(q));
  const done = pool.filter((q) => q.answered);
  const openMine = open.filter(mine);
  // Round P: inside every open group, questions NOBODY answered yet (has_memo === false/missing) come
  // before questions a colleague already answered (has_memo === true). Without has_memo: identical to before.
  const nobodyFirst = (list) => [
    ...shuffleArray(list.filter((q) => q.has_memo !== true)),
    ...shuffleArray(list.filter((q) => q.has_memo === true)),
  ];
  return [
    nobodyFirst(pool.filter((q) => isSiteFirst(q) && isFocusFirst(q))),
    nobodyFirst(pool.filter((q) => isSiteFirst(q) && !isFocusFirst(q))),
    nobodyFirst(pool.filter((q) => isFocusFirst(q) && !isSiteFirst(q))),
    preferTargeted
      ? [...nobodyFirst(openMine.filter(hasFunctie)), ...nobodyFirst(openMine.filter((q) => !hasFunctie(q)))]
      : nobodyFirst(openMine),
    nobodyFirst(open.filter((q) => !mine(q))),
    shuffleArray(done.filter(mine)),
    shuffleArray(done.filter((q) => !mine(q))),
  ].flat();
}

// Round T: an open question written for my own functie (functies non-empty and contains mine).
const targetedOpen = (q, title) => !q.answered && hasFunctie(q) && !!title && forMe(q, title);

// Round T: every set holds at least one targeted open question when one is available. Otherwise the
// last slot takes the first such question from the rest of the normal order; the one it replaces stays.
function withTargeted(set, rest, userTitle) {
  const title = (userTitle || "").trim().toLowerCase();
  if (!title || !set.length || set.some((q) => targetedOpen(q, title))) return set;
  const ids = new Set(set.map((q) => q.id));
  const pick = rest.find((q) => !ids.has(q.id) && targetedOpen(q, title));
  return pick ? [...set.slice(0, -1), pick] : set;
}

/**
 * Geeft de volgende set vragen plus de bijgewerkte "al getoond"-lijst.
 * De lijst wordt pas leeggemaakt als elke vraag uit de pool aan bod is geweest.
 */
export function nextQuestionSet(pool, shown = [], userTitle = "", size = PICK_SIZE) {
  const list = (Array.isArray(pool) ? pool : []).filter((q) => q && q.id);
  if (!list.length) return { questions: [], shown: [] };
  const ids = new Set(list.map((q) => q.id));
  const seen = new Set((shown || []).filter((id) => ids.has(id)));
  const remaining = list.filter((q) => !seen.has(q.id));
  const ordered = orderPool(remaining, userTitle, seen.size === 0);
  const picked = ordered.slice(0, size);
  if (picked.length >= size || picked.length === list.length) {
    const questions = withTargeted(picked, ordered.slice(size), userTitle);
    return { questions, shown: [...seen, ...questions.map((q) => q.id)] };
  }
  // De ronde is rond: begin opnieuw, maar herhaal niets binnen dit ene scherm.
  const pickedIds = new Set(picked.map((q) => q.id));
  const nextRound = orderPool(list.filter((q) => !pickedIds.has(q.id)), userTitle, false);
  const fill = size - picked.length;
  const questions = withTargeted([...picked, ...nextRound.slice(0, fill)], nextRound.slice(fill), userTitle);
  return { questions, shown: questions.map((q) => q.id) };
}
